import React from 'react';
import StartButton from '@/components/site/StartButton';
import SectionHeader from '@/components/site/SectionHeader';
import GoldText from '@/components/site/GoldText';
import WorkflowPreview from './WorkflowPreview';
import { workflows } from '@/content/workflows';
import { useRovingTabs } from '@/hooks/useRovingTabs';
import { cn } from '@/lib/utils';

/**
 * Section 02. A tablist: each problem statement swaps the preview panel, and
 * the selected workflow is carried into the discovery form.
 */
const WorkflowSelector = () => {
  const { selected, tabProps } = useRovingTabs(workflows.length);
  const workflow = workflows[selected];

  return (
    <section aria-labelledby="workflows-title" className="border-t border-rule">
      <div className="section-container">
        <SectionHeader
          id="workflows-title"
          eyebrow="01 / The work"
          title={
            <>
              What keeps pulling your team away from <GoldText>the work that matters?</GoldText>
            </>
          }
        >
          <p>
            Start with the task you repeat, the information you keep searching for, or the follow-up that keeps slipping
            through.
          </p>
        </SectionHeader>

        <div className="reveal mt-14 grid gap-8 lg:grid-cols-[1fr_1.25fr] lg:gap-12">
          <div
            role="tablist"
            aria-label="Common workflows"
            aria-orientation="vertical"
            className="flex flex-col border-t border-rule"
          >
            {workflows.map((w, i) => (
              <button
                key={w.id}
                {...tabProps(i)}
                id={`workflow-tab-${w.id}`}
                aria-controls="workflow-panel"
                className={cn(
                  'group grid grid-cols-[2.5rem_1fr_auto] items-baseline gap-3 border-b border-rule py-5 pl-1 text-left transition-colors',
                  i === selected ? 'text-softwhite' : 'text-mist hover:text-softwhite',
                )}
              >
                <span
                  aria-hidden="true"
                  className={cn('font-mono text-xs tracking-[0.18em]', i === selected ? 'gold-shine' : 'text-mist')}
                >
                  0{i + 1}
                </span>
                <span className="font-serif text-2xl leading-snug sm:text-[1.7rem]">{w.problem}</span>
                <span
                  aria-hidden="true"
                  className={cn(
                    'font-mono text-goldcore transition-opacity',
                    i === selected ? 'opacity-100' : 'opacity-0 group-hover:opacity-40',
                  )}
                >
                  →
                </span>
              </button>
            ))}
          </div>

          <div
            id="workflow-panel"
            role="tabpanel"
            aria-labelledby={`workflow-tab-${workflow.id}`}
            className="surface surface-lit flex flex-col gap-6 p-6 sm:p-8"
          >
            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <p className="meta-label mb-2">Example opportunity</p>
                <p className="font-serif text-2xl leading-snug">{workflow.opportunity}</p>
              </div>
              <div>
                <p className="meta-label mb-2">Preview output</p>
                <p className="text-mist">{workflow.previewOutput}</p>
              </div>
            </div>
            <WorkflowPreview workflow={workflow} />
            <div className="mt-auto flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="max-w-sm text-sm text-mist">
                These are starting points. Your discovery call helps determine which workflow is suitable for a pilot.
              </p>
              <StartButton interest={workflow.id} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WorkflowSelector;
